import { Injectable } from "@angular/core";
import { Assertion, db, ImageType, JobIssue, Media } from "../../../core/database/db";
import { UploadManagerService } from "../../../core/sync/image-sync/upload-manager.service";

@Injectable({ providedIn: "root" })
export class MediaService {

  constructor(private uploadManagerService: UploadManagerService) { }

  async getMediaForJobIssues(jobIssues: JobIssue[]): Promise<Media[]> {
    const issueIdentifiers = jobIssues.map(issue => issue.identifier);
    return await db.media.where("parentIdentifier").anyOf(issueIdentifiers)
      .toArray();
  }

  async getMediaForAssertions(assertions: Assertion[]): Promise<Media[]> {
    const assertionIdentifiers = assertions.map(assertion => assertion.identifier);
    return await db.media.where("parentIdentifier").anyOf(assertionIdentifiers)
      .toArray();
  }

  async getActiveMediaForParent(parentIdentifier: string): Promise<Media[]> {
    return await db.media
      .where("parentIdentifier")
      .equals(parentIdentifier)
      .and(m => !m.deleted)
      .toArray();
  }

  async getHydratedMediaByFileIdentifier(fileIdentifier: string): Promise<Media | undefined> {
    return await db.media
      .where("fileIdentifier")
      .equals(fileIdentifier)
      .and(m => !!m.base64Image && !m.deleted)
      .first();
  }

  async bulkAddMedia(media: Media[]): Promise<number[]> {
    const addedMediaIds = await db.media.bulkAdd(media, {
      allKeys: true
    });
    if (addedMediaIds.length > 0) {
      await this.uploadManagerService.markFilesReadyForUpload(addedMediaIds);
    }
    return addedMediaIds;
  }

  async softDeleteMediaForParent(parentIdentifier: string, imageType?: ImageType): Promise<void> {
    await db.media
      .where("parentIdentifier")
      .equals(parentIdentifier)
      .and(m => (!imageType || m.imageType === imageType) && !m.deleted)
      .modify({ deleted: 1, deletedAt: new Date() });
  }

  async restoreOriginalIssueMedia(parentIdentifier: string): Promise<Media[]> {
    const deletedIssuePhotos = await db.media
      .where("parentIdentifier")
      .equals(parentIdentifier)
      .and(m => m.imageType === ImageType.Issue && !!m.deleted)
      .toArray();

    if (deletedIssuePhotos.length > 0) {
      // Soft-delete the currently active Issue photos (copies / new additions)
      const activeIds = (await db.media
        .where("parentIdentifier")
        .equals(parentIdentifier)
        .and(m => m.imageType === ImageType.Issue && !m.deleted)
        .toArray())
        .map(m => m.id)
        .filter((id): id is number => id !== undefined);

      if (activeIds.length > 0) {
        await db.media
          .where("id")
          .anyOf(activeIds)
          .modify({ deleted: 1, deletedAt: new Date() });
      }

      // Restore the original soft-deleted Issue photos
      const deletedIds = deletedIssuePhotos
        .map(m => m.id)
        .filter((id): id is number => id !== undefined);
      await db.media
        .where("id")
        .anyOf(deletedIds)
        .modify({ deleted: 0, deletedAt: undefined });
    }

    return await db.media
      .where("parentIdentifier")
      .equals(parentIdentifier)
      .and(m => m.imageType === ImageType.Issue && !m.deleted)
      .toArray();
  }
}